import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Modal, Progress, message } from "antd";
import { CloseCircleOutlined, FileTextOutlined, SendOutlined } from "@ant-design/icons";
import ExportToolbar from "../../components/ExportToolbar";
import { useLocationContext } from "context/LocationContext";
import useAuth from "../../hooks/useAuth";
import DpatRankingTable from "./components/DpatRankingTable";
import DpatCiRankingTable from "./components/DpatCiRankingTable";
import RegionRankingTable from "./components/RegionRankingTable";
import DpatMetricCard from "./components/DpatMetricCard";
import DpatAnalyticsSection from "./components/DpatAnalyticsSection";
import DpatAssessmentWorkflowSection from "./components/DpatAssessmentWorkflowSection";
import DpatBulkAssessmentStarter from "./components/DpatBulkAssessmentStarter";
import DpatExportButtons from "./components/DpatExportButtons";
import { DPAT_BRAND } from "./dpatChartTheme";
import {
  flattenDistrictsWithRegions,
  fetchDistrictDpatScores,
  fetchSystemScoreRows,
  summarizeDpatScores,
  buildNationalDistrictRanking,
  buildRegionalDistrictRankings,
  filterDpatScoreRows,
  buildRegionFilterOptions,
  DPAT_DATA_FILTER_OPTIONS,
  mapRowsToTechnicalTeamScores,
  mapRowsToSystemCiScores,
} from "./dpatScoreStats";
import {
  fetchDistrictPetitions,
  summarizePetitions,
  flattenDistrictsWithRegionsForPetitions,
} from "../dpat-petitions/petitionWorkflowStats";
import {
  bulkCloseAssessments,
  bulkReleaseAssessmentsToDistrict,
  fetchDistrictDpatAssessments,
  isEligibleForBulkStart,
  isEligibleForClose,
  isEligibleForQaRelease,
  summarizeDpatAssessmentWorkflow,
} from "./dpatAssessmentWorkflowStats";
import {
  buildDashboardKpiRows,
  buildDistrictRankingRows,
  buildRegionRankingRows,
  buildCiRankingRows,
  downloadCsvFromRows,
  downloadDashboardAsPdf,
  downloadNodeAsPdf,
} from "./dpatDashboardExport";
import {
  downloadDashboardExcel,
  downloadDashboardWord,
} from "./dpatDashboardOfficeExport";

const DPAT_YEAR_START = 2020;

const DPAT_YEAR_OPTIONS = (() => {
  const endYear = new Date().getFullYear();
  const options = [];
  for (let year = endYear; year >= DPAT_YEAR_START; year -= 1) {
    options.push({ label: String(year), value: String(year) });
  }
  return options;
})();

const VIEW_TABS = [
  { id: "national", label: "National district ranking" },
  { id: "regional", label: "Within-region district ranking" },
  { id: "regions", label: "Region ranking" },
];

const tabBtnClass = (active) =>
  `rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
    active
      ? "bg-[#16325a] text-white shadow-sm"
      : "bg-muted text-muted-foreground hover:bg-[#16325a]/10 hover:text-[#16325a]"
  }`;

const DpatDashboard = () => {
  const { orgTree, isOrgTreeLoading } = useLocationContext();
  const { user } = useAuth();
  const currentYear = String(new Date().getFullYear());
  const defaultYear =
    DPAT_YEAR_OPTIONS.find((o) => o.value === currentYear) || DPAT_YEAR_OPTIONS[0];

  const [reportingYear, setReportingYear] = useState(defaultYear?.value || currentYear);
  const [scoreRows, setScoreRows] = useState([]);
  const [systemScoreRows, setSystemScoreRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [systemLoading, setSystemLoading] = useState(false);
  const [rankingScoreSource, setRankingScoreSource] = useState("final");
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState("national");
  const [regionFilter, setRegionFilter] = useState("all");
  const [dataFilter, setDataFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedRegionDetail, setSelectedRegionDetail] = useState("all");
  const [petitionSummary, setPetitionSummary] = useState(null);
  const [petitionsLoading, setPetitionsLoading] = useState(false);
  const [assessmentRows, setAssessmentRows] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [assessmentLoadProgress, setAssessmentLoadProgress] = useState({
    loaded: 0,
    total: 0,
    percent: 0,
  });
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseProgress, setReleaseProgress] = useState({ processed: 0, total: 0, current: null });
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeProgress, setCloseProgress] = useState({ processed: 0, total: 0, current: null });
  const [startAllLoading, setStartAllLoading] = useState(false);
  const [startAllProgress, setStartAllProgress] = useState({ processed: 0, total: 0, current: null });
  const [bulkStartRows, setBulkStartRows] = useState([]);
  const dashboardExportRef = useRef(null);
  const rankingTableRef = useRef(null);
  const ciRankingTableRef = useRef(null);
  const regionTableRef = useRef(null);

  const isDpatQaUser = Boolean(
    user?.user?.userRoles?.some((role) =>
      ["DPAT QUALITY ASSURANCE", "Superuser"].includes(role.name),
    ),
  );
  const isOfsamUser =
    String(user?.user?.username || "").trim().toLowerCase() === "ofsam";

  const districtRows = useMemo(() => flattenDistrictsWithRegions(orgTree), [orgTree]);
  const districtRowsKey = useMemo(
    () => districtRows.map((row) => row.districtId).join("|"),
    [districtRows],
  );
  const districtRowsRef = useRef(districtRows);
  districtRowsRef.current = districtRows;
  const petitionDistrictRows = useMemo(
    () => flattenDistrictsWithRegionsForPetitions(orgTree),
    [orgTree],
  );

  const nationalRanking = useMemo(
    () => buildNationalDistrictRanking(scoreRows),
    [scoreRows],
  );

  const regionalGroups = useMemo(
    () => buildRegionalDistrictRankings(scoreRows),
    [scoreRows],
  );

  const summary = useMemo(() => summarizeDpatScores(scoreRows), [scoreRows]);

  const assessmentWorkflowSummary = useMemo(
    () => summarizeDpatAssessmentWorkflow(assessmentRows),
    [assessmentRows],
  );

  const refreshAssessmentRows = useCallback(async (signal) => {
    const currentDistrictRows = districtRowsRef.current;
    if (!currentDistrictRows.length) {
      setAssessmentRows([]);
      setAssessmentLoadProgress({ loaded: 0, total: 0, percent: 0 });
      return;
    }
    const rows = await fetchDistrictDpatAssessments(
      currentDistrictRows,
      reportingYear,
      signal,
    );
    if (!signal?.aborted) setAssessmentRows(rows);
  }, [reportingYear]);

  useEffect(() => {
    if (!districtRows.length || isOrgTreeLoading) {
      setScoreRows([]);
      return;
    }

    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      setScoreRows([]);
      setSystemScoreRows([]);
      setRankingScoreSource("final");
      try {
        const rows = await fetchDistrictDpatScores(
          districtRows,
          reportingYear,
          controller.signal,
        );
        if (!controller.signal.aborted) {
          setScoreRows(rows);
          const systemRows = await fetchSystemScoreRows(
            districtRows,
            reportingYear,
            controller.signal,
          );
          if (!controller.signal.aborted) setSystemScoreRows(systemRows);
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load DPAT scores.");
        setScoreRows([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [districtRowsKey, reportingYear, isOrgTreeLoading]);

  useEffect(() => {
    if (!petitionDistrictRows.length || isOrgTreeLoading) {
      setPetitionSummary(null);
      return;
    }

    const controller = new AbortController();

    (async () => {
      setPetitionsLoading(true);
      try {
        const rows = await fetchDistrictPetitions(
          petitionDistrictRows,
          reportingYear,
          controller.signal,
        );
        if (!controller.signal.aborted) {
          setPetitionSummary(summarizePetitions(rows));
        }
      } catch {
        if (!controller.signal.aborted) setPetitionSummary(null);
      } finally {
        if (!controller.signal.aborted) setPetitionsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [petitionDistrictRows, reportingYear, isOrgTreeLoading]);

  useEffect(() => {
    if (isOrgTreeLoading) return;

    const currentDistrictRows = districtRowsRef.current;
    if (!currentDistrictRows.length) {
      setAssessmentRows([]);
      return;
    }

    const controller = new AbortController();

    (async () => {
      setAssessmentsLoading(true);
      try {
        const rows = await fetchDistrictDpatAssessments(
          currentDistrictRows,
          reportingYear,
          controller.signal,
        );
        if (!controller.signal.aborted) setAssessmentRows(rows);
      } catch {
        if (!controller.signal.aborted) setAssessmentRows([]);
      } finally {
        if (!controller.signal.aborted) setAssessmentsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [reportingYear, isOrgTreeLoading]);

  const handleReleaseAssessments = () => {
    const eligibleCount = assessmentWorkflowSummary.qaReleaseEligible;
    if (eligibleCount === 0) {
      message.warning("No districts have QA-reviewed assessments ready to release for this year.");
      return;
    }

    Modal.confirm({
      title: "Release assessments to districts",
      content: (
        <p>
          This will send the DPAT {reportingYear} assessment to {" "}
          <strong>{eligibleCount}</strong> district assessment{eligibleCount === 1 ? "" : "s"}{" "}
          in {reportingYear}. Districts will be able to view their completed scores and submit
          petitions. Continue?
        </p>
      ),
      okText: "Release",
      cancelText: "Cancel",
      okButtonProps: { style: { backgroundColor: "#16325a", borderColor: "#16325a" } },
      onOk: async () => {
        setReleaseLoading(true);
        const eligibleRows = assessmentRows.filter(
          (row) => isEligibleForQaRelease(row.status) && row.assessment?.id,
        );
        setReleaseProgress({ processed: 0, total: eligibleRows.length, current: null });

        try {
          const result = await bulkReleaseAssessmentsToDistrict({
            rows: eligibleRows,
            year: reportingYear,
            user,
            onProgress: (progress) => setReleaseProgress(progress),
            onRowReleased: (updatedRow) => {
              setAssessmentRows((prev) =>
                prev.map((row) =>
                  row.districtId === updatedRow.districtId ? { ...row, ...updatedRow } : row,
                ),
              );
            },
          });

          if (result.failed.length === 0) {
            message.success(
              `Released ${result.succeeded.length} assessment${result.succeeded.length === 1 ? "" : "s"} to districts.`,
            );
          } else if (result.succeeded.length === 0) {
            message.error(`Failed to release assessments for ${result.failed.length} district(s).`);
          } else {
            message.warning(
              `Released ${result.succeeded.length} assessment(s); ${result.failed.length} failed.`,
            );
          }
        } catch (error) {
          message.error(error?.response?.data?.message || error?.message || "Bulk release failed.");
        } finally {
          setReleaseLoading(false);
          setReleaseProgress({ processed: 0, total: 0, current: null });
        }
      },
    });
  };

  const closeEligibleCount = useMemo(
    () =>
      assessmentRows.filter((row) => isEligibleForClose(row.status) && row.assessment?.id).length,
    [assessmentRows],
  );

  const handleCloseAssessments = () => {
    if (closeEligibleCount === 0) {
      message.warning("No released assessments are ready to close for this year.");
      return;
    }

    Modal.confirm({
      title: "Close all DPAT assessments",
      content: (
        <p>
          This will close the DPAT {reportingYear} assessment for{" "}
          <strong>{closeEligibleCount}</strong> released district
          {closeEligibleCount === 1 ? "" : "s"}. Continue?
        </p>
      ),
      okText: "Close all",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: async () => {
        setCloseLoading(true);
        const eligibleRows = assessmentRows.filter(
          (row) => isEligibleForClose(row.status) && row.assessment?.id,
        );
        setCloseProgress({ processed: 0, total: eligibleRows.length, current: null });

        try {
          const result = await bulkCloseAssessments({
            rows: eligibleRows,
            year: reportingYear,
            user,
            onProgress: (progress) => setCloseProgress(progress),
            onRowClosed: (updatedRow) => {
              setAssessmentRows((prev) =>
                prev.map((row) =>
                  row.districtId === updatedRow.districtId ? { ...row, ...updatedRow } : row,
                ),
              );
            },
          });

          if (result.failed.length === 0) {
            message.success(
              `Closed ${result.succeeded.length} assessment${result.succeeded.length === 1 ? "" : "s"}.`,
            );
          } else if (result.succeeded.length === 0) {
            message.error(`Failed to close assessments for ${result.failed.length} district(s).`);
          } else {
            message.warning(
              `Closed ${result.succeeded.length} assessment(s); ${result.failed.length} failed.`,
            );
          }
        } catch (error) {
          message.error(error?.response?.data?.message || error?.message || "Bulk close failed.");
        } finally {
          setCloseLoading(false);
          setCloseProgress({ processed: 0, total: 0, current: null });
        }
      },
    });
  };

  const handleStartAllAssessments = () => {
    const eligibleCount = assessmentWorkflowSummary.bulkStartEligible ?? 0;
    if (eligibleCount === 0) {
      message.warning("All districts already have a DPAT assessment for this year.");
      return;
    }

    Modal.confirm({
      title: "Start all DPAT assessments",
      content: (
        <p>
          This will load and save the full DPAT assessment snapshot for{" "}
          <strong>{eligibleCount}</strong> district{eligibleCount === 1 ? "" : "s"} in{" "}
          {reportingYear}, and start any assessment that has not already started. Continue?
        </p>
      ),
      okText: "Start all",
      cancelText: "Cancel",
      okButtonProps: { style: { backgroundColor: "#16325a", borderColor: "#16325a" } },
      onOk: () => {
        const eligibleRows = assessmentRows.filter((row) =>
          isEligibleForBulkStart(row.status, row.hasStartSnapshot),
        );
        setStartAllLoading(true);
        setStartAllProgress({
          processed: 0,
          total: eligibleRows.length,
          current: eligibleRows[0]?.districtName || null,
        });
        setBulkStartRows(eligibleRows);
      },
    });
  };

  const handleBulkStartFinished = useCallback(async (result) => {
    setBulkStartRows([]);
    try {
      await refreshAssessmentRows(new AbortController().signal);
    } finally {
      setStartAllLoading(false);
      setStartAllProgress({ processed: 0, total: 0, current: null });
    }

    if (result.failed.length === 0) {
      message.success(
        `Started and saved snapshots for ${result.succeeded.length} district assessment${result.succeeded.length === 1 ? "" : "s"}.`,
      );
    } else if (result.succeeded.length === 0) {
      message.error(`Failed to start and save snapshots for ${result.failed.length} district(s).`);
    } else {
      message.warning(
        `Started and saved ${result.succeeded.length} assessment snapshot(s); ${result.failed.length} failed.`,
      );
    }
  }, [refreshAssessmentRows]);

  useEffect(() => {
    setRegionFilter("all");
    setDataFilter("all");
    setSearch("");
    setSelectedRegionDetail("all");
    setRankingScoreSource("final");
  }, [reportingYear]);

  const regionOptions = useMemo(() => buildRegionFilterOptions(scoreRows), [scoreRows]);

  const filteredNationalRows = useMemo(
    () => filterDpatScoreRows(nationalRanking, { regionKey: regionFilter, dataFilter, search }),
    [nationalRanking, regionFilter, dataFilter, search],
  );

  const systemRanking = useMemo(
    () => buildNationalDistrictRanking(systemScoreRows),
    [systemScoreRows],
  );

  const filteredSystemRows = useMemo(
    () => filterDpatScoreRows(systemRanking, { regionKey: regionFilter, dataFilter, search }),
    [systemRanking, regionFilter, dataFilter, search],
  );

  const technicalRanking = useMemo(
    () => buildNationalDistrictRanking(mapRowsToTechnicalTeamScores(scoreRows)),
    [scoreRows],
  );

  const filteredTechnicalRows = useMemo(
    () => filterDpatScoreRows(technicalRanking, { regionKey: regionFilter, dataFilter, search }),
    [technicalRanking, regionFilter, dataFilter, search],
  );

  const nationalRowsForSource =
    rankingScoreSource === "system"
      ? filteredSystemRows
      : rankingScoreSource === "technical"
        ? filteredTechnicalRows
        : filteredNationalRows;

  const filteredCiRows = useMemo(() => {
    const sourceRows =
      rankingScoreSource === "system"
        ? mapRowsToSystemCiScores(scoreRows)
        : rankingScoreSource === "technical"
          ? mapRowsToTechnicalTeamScores(scoreRows)
          : scoreRows;
    return filterDpatScoreRows(sourceRows, { regionKey: regionFilter, dataFilter, search });
  }, [rankingScoreSource, scoreRows, regionFilter, dataFilter, search]);

  const handleRankingScoreSourceChange = useCallback(async (source) => {
    setRankingScoreSource(source);
    if (source !== "system" || systemScoreRows.length) return;
    setSystemLoading(true);
    try {
      const rows = await fetchSystemScoreRows(districtRows, reportingYear);
      setSystemScoreRows(rows);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || "Failed to load System scores.");
      setRankingScoreSource("final");
    } finally {
      setSystemLoading(false);
    }
  }, [districtRows, reportingYear, systemScoreRows.length]);

  const selectedRegionalGroup = useMemo(() => {
    if (selectedRegionDetail === "all") return null;
    return regionalGroups.find((g) => g.regionKey === selectedRegionDetail) || null;
  }, [regionalGroups, selectedRegionDetail]);

  const filteredRegionalDistricts = useMemo(() => {
    if (!selectedRegionalGroup) return [];
    return filterDpatScoreRows(selectedRegionalGroup.districts, { dataFilter, search });
  }, [selectedRegionalGroup, dataFilter, search]);

  const regionalDetailOptions = useMemo(
    () => [
      { value: "all", label: "Select a region…" },
      ...regionalGroups.map((g) => ({ value: g.regionKey, label: g.regionName })),
    ],
    [regionalGroups],
  );

  const treeReady = !isOrgTreeLoading;
  const dataReady = treeReady && districtRows.length > 0 && !loading && scoreRows.length === districtRows.length;

  const headlineMetrics = [
    {
      title: "Districts Assemblies Assessed",
      value: String(summary.assessedCount),
      unit: `/ ${districtRows.length || "—"}`,
      icon: "ClipboardCheck",
      hint: "Assemblies with DPAT scores",
      progress: districtRows.length
        ? Math.round((summary.assessedCount / districtRows.length) * 100)
        : 0,
    },
    {
      title: "National average",
      value: summary.nationalAvg != null ? summary.nationalAvg.toFixed(1) : "—",
      unit: summary.nationalAvg != null ? "%" : "",
      icon: "TrendingUp",
      hint: "Final Outcome average",
      progress: summary.nationalAvg != null ? summary.nationalAvg : undefined,
    },
    {
      title: "SDI average",
      value: summary.sdiAvg != null ? summary.sdiAvg.toFixed(1) : "—",
      unit: summary.sdiAvg != null ? "%" : "",
      icon: "BarChart3",
      hint: "Service delivery indicators",
      progress: summary.sdiAvg != null ? summary.sdiAvg : undefined,
    },
    {
      title: "PI average",
      value: summary.piAvg != null ? summary.piAvg.toFixed(1) : "—",
      unit: summary.piAvg != null ? "%" : "",
      icon: "Target",
      hint: "Performance indicators",
      progress: summary.piAvg != null ? summary.piAvg : undefined,
    },
    {
      title: "CI average",
      value: summary.ciAvg != null ? summary.ciAvg.toFixed(1) : "—",
      unit: summary.ciAvg != null ? "%" : "",
      icon: "CheckCircle",
      hint: "Compliance indicators",
      progress: summary.ciAvg != null ? summary.ciAvg : undefined,
    },
    {
      title: "Yet to be assessed",
      value: String(Math.max(0, districtRows.length - summary.assessedCount)),
      unit: "districts",
      icon: "AlertCircle",
      hint: "Still without scores",
      progress: districtRows.length
        ? Math.round(((districtRows.length - summary.assessedCount) / districtRows.length) * 100)
        : 0,
    },
  ];

  const analyticsBlock = (
    <DpatAnalyticsSection
      summary={summary}
      reportingYear={reportingYear}
      loading={loading && districtRows.length > 0}
      dataReady={dataReady}
    />
  );

  const handleDashboardPdf = async () => {
    if (!dashboardExportRef.current) return;
    await downloadDashboardAsPdf(
      dashboardExportRef.current,
      `dpat-dashboard-${reportingYear}.pdf`,
    );
  };

  const dashboardExportPayload = {
    year: reportingYear,
    summary,
    districts: nationalRanking,
    regions: summary.regionRanking,
    workflow: assessmentWorkflowSummary,
    districtCount: districtRows.length,
  };

  const handleDashboardWord = async () => {
    await downloadDashboardWord(dashboardExportPayload);
  };

  const handleDashboardExcel = async () => {
    await downloadDashboardExcel(dashboardExportPayload);
  };

  const handleDashboardCsv = () => {
    downloadCsvFromRows(
      [
        ...buildDashboardKpiRows(summary, reportingYear),
        [],
        ["National district ranking"],
        ...buildDistrictRankingRows(nationalRanking),
        [],
        ["National Compliance Indicators (CI) Ranking"],
        ...buildCiRankingRows(nationalRanking),
        [],
        ["National region ranking"],
        ...buildRegionRankingRows(summary.regionRanking),
        [],
        ["Assessment workflow"],
        ["Stage", "Districts"],
        ["Yet to start", assessmentWorkflowSummary?.yetToStart ?? 0],
        ["Start", assessmentWorkflowSummary?.inProgress ?? 0],
        ["Pending", assessmentWorkflowSummary?.pendingReview ?? 0],
        ["Reviewed", assessmentWorkflowSummary?.reviewed ?? 0],
        ["Completed", assessmentWorkflowSummary?.completed ?? 0],
        ["Closed", assessmentWorkflowSummary?.closed ?? 0],
      ],
      `dpat-dashboard-${reportingYear}.csv`,
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {bulkStartRows.length > 0 && (
        <DpatBulkAssessmentStarter
          rows={bulkStartRows}
          year={reportingYear}
          user={user}
          onProgress={setStartAllProgress}
          onFinished={handleBulkStartFinished}
        />
      )}
      <ExportToolbar />
      <main ref={dashboardExportRef} className="main-content">
        <div data-export-hide className="mb-6 md:mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              DPAT Dashboard
            </h1>
            <p className="text-sm md:text-base text-muted-foreground max-w-3xl">
              National KPIs, analytics, and rankings based on each district&apos;s DPAT Final
              Outcome. Compare districts nationally, within their region, and region averages.
              Assessment year: {reportingYear}.
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 md:w-auto">
            <div data-export-hide className="w-full md:w-56">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                Assessment year
              </label>
              <select
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#16325a]/25"
                value={reportingYear}
                onChange={(e) => setReportingYear(e.target.value)}
              >
                {DPAT_YEAR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <DpatExportButtons
              label="Download dashboard"
              onPdf={handleDashboardPdf}
              onWord={handleDashboardWord}
              onExcel={handleDashboardExcel}
              onCsv={handleDashboardCsv}
              disabled={loading || !scoreRows.length}
            />
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading && districtRows.length > 0 && (
          <div className="mb-6 rounded-xl border border-border bg-card px-4 py-4 shadow-sm md:px-5">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">
                Loading DPAT Final Outcome scores for {reportingYear}...
              </span>
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#16325a] border-t-transparent"></div>
            </div>
          </div>
        )}

        {!treeReady && (
          <p className="mb-6 text-sm text-muted-foreground">Loading organisation tree…</p>
        )}

        {treeReady && districtRows.length === 0 && (
          <p className="mb-6 rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
            No districts found in the location tree.
          </p>
        )}

        {(isDpatQaUser || isOfsamUser) && (
          <section data-export-hide className="mb-6 rounded-xl border border-border bg-card p-5 shadow-sm md:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-4">
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
                  style={{ backgroundColor: "rgba(22, 50, 90, 0.08)" }}
                >
                  <SendOutlined style={{ fontSize: 22, color: "#16325a" }} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground md:text-xl">
                    DPAT {reportingYear} Assessment Release
                  </h2>
                  <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                    The DPAT {reportingYear} assessment has been completed by the Technical
                    Team and reviewed by the Quality Assurance Team. And it is ready to be
                    released to the MMDAs.
                  </p>
                  {assessmentsLoading ? (
                    <></>
                  ) : (
                    <p className="mt-2 text-sm text-foreground">
                      <span className="font-semibold text-amber-700">
                        {assessmentWorkflowSummary.qaReleaseEligible}
                      </span>{" "}
                      district{assessmentWorkflowSummary.qaReleaseEligible === 1 ? "" : "s"} ready to
                      release
                      {" · "}
                      <span className="font-semibold text-indigo-700">
                        {assessmentWorkflowSummary.counts.Reviewed ?? 0}
                      </span>{" "}
                      QA reviewed
                      {" · "}
                      <span className="font-semibold text-emerald-700">
                        {assessmentWorkflowSummary.counts.Completed ?? 0}
                      </span>{" "}
                      already released
                    </p>
                  )}
                  {releaseLoading && releaseProgress.total > 0 && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      Releasing {releaseProgress.processed} of {releaseProgress.total}
                      {releaseProgress.current ? ` — ${releaseProgress.current}` : ""}…
                    </p>
                  )}
                  {closeLoading && closeProgress.total > 0 && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      Closing {closeProgress.processed} of {closeProgress.total}
                      {closeProgress.current ? ` — ${closeProgress.current}` : ""}…
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-export-hide>
                {isDpatQaUser && (
                  <Button
                    type="primary"
                    size="large"
                    icon={<SendOutlined />}
                    loading={releaseLoading}
                    disabled={
                      assessmentsLoading ||
                      assessmentWorkflowSummary.qaReleaseEligible === 0 ||
                      ((assessmentWorkflowSummary.reviewed ?? 0) +
                        (assessmentWorkflowSummary.completed ?? 0) +
                        (assessmentWorkflowSummary.closed ?? 0)) <
                        (assessmentWorkflowSummary.total || 261)
                    }
                    onClick={handleReleaseAssessments}
                    style={{
                      backgroundColor: "#338e06",
                      borderColor: "#338e06",
                      color: "#ffffff",
                      minWidth: 240,
                    }}
                  >
                    Release Assessment to districts
                  </Button>
                )}
                {isDpatQaUser && (
                  <Button
                    type="primary"
                    danger
                    size="large"
                    icon={<CloseCircleOutlined />}
                    loading={closeLoading}
                    disabled={assessmentsLoading || closeEligibleCount === 0}
                    onClick={handleCloseAssessments}
                    style={{
                      backgroundColor: "#cf1322",
                      borderColor: "#cf1322",
                      color: "#ffffff",
                      minWidth: 240,
                    }}
                  >
                    Close all DPAT Assessment
                  </Button>
                )}
              </div>
            </div>
          </section>
        )}

        <section data-export-hide className="mb-8 rounded-xl border border-border bg-card p-5 shadow-sm md:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
                style={{ backgroundColor: "rgba(22, 50, 90, 0.08)" }}
              >
                <FileTextOutlined style={{ fontSize: 22, color: "#16325a" }} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground md:text-xl">
                  DPAT {reportingYear} District petitions
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                  Review petitions submitted by districts after DPAT {reportingYear} assessment completion.
                  Accept or reject submissions from the DPAT Secretary.
                </p>
                {petitionsLoading ? (
                  <></>
                ) : petitionSummary ? (
                  <p className="mt-2 text-sm text-foreground">
                    <span className="font-semibold">{petitionSummary.submittedCount}</span> submitted
                    {" · "}
                    <span className="font-semibold text-amber-700">{petitionSummary.pendingCount}</span> pending
                    {" · "}
                    <span className="font-semibold text-emerald-700">{petitionSummary.acceptedCount}</span> accepted
                    {" · "}
                    <span className="font-semibold text-red-700">{petitionSummary.rejectedCount}</span> rejected
                  </p>
                ) : null}
              </div>
            </div>
            <Link to={`/ghana/dpat-petitions?year=${reportingYear}`} data-export-hide>
              <Button
                type="primary"
                size="large"
                style={{ backgroundColor: "#16325a", borderColor: "#16325a", minWidth: 200 }}
              >
                Access All Petitions
              </Button>
            </Link>
          </div>
        </section>

        <DpatAssessmentWorkflowSection
          summary={assessmentWorkflowSummary}
          reportingYear={reportingYear}
          loading={assessmentsLoading}
          assessmentRows={assessmentRows}
        />

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {loading && districtRows.length > 0
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-36 animate-pulse rounded-xl border border-border bg-card" />
              ))
            : headlineMetrics.map((m, i) => (
                <DpatMetricCard key={m.title} {...m} styleIndex={i} />
              ))}
        </div>

        <div data-export-hide className="mb-4 flex flex-wrap gap-2 border-b border-border pb-2">
          {VIEW_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveView(tab.id)}
              className={tabBtnClass(activeView === tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeView === "national" && (
          <div className="space-y-6">
            <DpatRankingTable
              rows={nationalRowsForSource}
              loading={
                rankingScoreSource === "system"
                  ? systemLoading || (!systemScoreRows.length && loading)
                  : loading && districtRows.length > 0
              }
              reportingYear={reportingYear}
              regionFilter={regionFilter}
              onRegionFilterChange={setRegionFilter}
              dataFilter={dataFilter}
              onDataFilterChange={setDataFilter}
              search={search}
              onSearchChange={setSearch}
              regionOptions={regionOptions}
              dataOptions={DPAT_DATA_FILTER_OPTIONS}
              title="National district ranking"
              subtitle={
                rankingScoreSource === "system"
                  ? `Ranked by System Score % — all regions, ${reportingYear}`
                  : rankingScoreSource === "technical"
                    ? `Ranked by Technical Team Score % — all regions, ${reportingYear}`
                    : `Ranked by Final Outcome % — all regions, ${reportingYear}`
              }
              scoreSource={rankingScoreSource}
              onScoreSourceChange={handleRankingScoreSourceChange}
              tableCaptureRef={rankingTableRef}
            />
            <DpatCiRankingTable
              rows={filteredCiRows}
              loading={
                rankingScoreSource === "system"
                  ? systemLoading || (!systemScoreRows.length && loading)
                  : loading && districtRows.length > 0
              }
              reportingYear={reportingYear}
              tableCaptureRef={ciRankingTableRef}
              scoreSource={rankingScoreSource}
              onScoreSourceChange={handleRankingScoreSourceChange}
            />
            {analyticsBlock}
          </div>
        )}

        {activeView === "regional" && (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              See how districts rank within their own region only — useful for RCC teams comparing
              peers in the same region.
            </p>
            <div className="max-w-md" data-export-hide>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Region
              </label>
              <select
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#16325a]/25"
                value={selectedRegionDetail}
                onChange={(e) => setSelectedRegionDetail(e.target.value)}
              >
                {regionalDetailOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {selectedRegionDetail === "all" ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {regionalGroups.map((grp) => (
                  <button
                    key={grp.regionKey}
                    type="button"
                    onClick={() => setSelectedRegionDetail(grp.regionKey)}
                    className="rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-colors hover:border-[#16325a]/30 hover:bg-[#16325a]/5"
                  >
                    <h4 className="font-semibold text-foreground">{grp.regionName}</h4>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {grp.assessedCount} / {grp.districtCount} Districts Assemblies Assessed
                    </p>
                    {grp.regionAveragePercent != null && (
                      <p className="mt-2 text-lg font-bold" style={{ color: DPAT_BRAND }}>
                        Avg {grp.regionAveragePercent.toFixed(1)}%
                      </p>
                    )}
                  </button>
                ))}
              </div>
            ) : selectedRegionalGroup ? (
              <>
                <div className="rounded-xl border border-border bg-card px-4 py-4 md:px-6">
                  <h3 className="text-lg font-semibold text-foreground">
                    {selectedRegionalGroup.regionName}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Regional average:{" "}
                    {selectedRegionalGroup.regionAveragePercent != null
                      ? `${selectedRegionalGroup.regionAveragePercent.toFixed(1)}%`
                      : "—"}{" "}
                    · {selectedRegionalGroup.assessedCount} district(s) with scores
                  </p>
                </div>
                <DpatRankingTable
                  rows={filteredRegionalDistricts}
                  loading={loading && districtRows.length > 0}
                  reportingYear={reportingYear}
                  regionFilter="all"
                  onRegionFilterChange={() => {}}
                  dataFilter={dataFilter}
                  onDataFilterChange={setDataFilter}
                  search={search}
                  onSearchChange={setSearch}
                  regionOptions={[{ value: "all", label: "All regions" }]}
                  dataOptions={DPAT_DATA_FILTER_OPTIONS}
                  showRegion={false}
                  title={`${selectedRegionalGroup.regionName} — district ranking`}
                  subtitle="Ranks apply within this region only"
                  tableCaptureRef={rankingTableRef}
                />
              </>
            ) : null}
            {analyticsBlock}
          </div>
        )}

        {activeView === "regions" && (
          <>
            <RegionRankingTable
              rows={summary.regionRanking}
              loading={loading && districtRows.length > 0}
              reportingYear={reportingYear}
              tableCaptureRef={regionTableRef}
            />
            {analyticsBlock}
          </>
        )}
      </main>
    </div>
  );
};

export default DpatDashboard;